import { createElement } from 'react';
import { recipeDetailsUrl } from '../../helpers/link.helper';
import { scrollPaneUpTo } from '../results/file-diff-view/scroll-pane.helper';
import { ChooseOrgContent } from './choose-org-content.component';
import type { EngineTourConfig, EngineTourStep } from './tour-engine.types';

export const DAY_ZERO_TOUR_ID = 'day-zero';
const DAY_ZERO_STORAGE_KEY = 'moderne-tour-day-zero';

/**
 * The recipe the Day Zero tour guides the user to run. During the tour only
 * this recipe is selectable from the search results (it is the sole spotlighted
 * row), so a first-time user can't wander into an arbitrary recipe.
 */
export const DAY_ZERO_RECIPE_ID =
  'org.openrewrite.staticanalysis.CommonStaticAnalysis';

/**
 * The four high-level phases shown in the progress dots. Ranges are inclusive
 * indices into `steps`.
 */
const dayZeroHighLevelSteps = [
  { label: 'Select your organization', range: [0, 1] },
  { label: 'Find a recipe', range: [2, 4] },
  { label: 'Run the recipe', range: [5, 7] },
  { label: 'Review the results', range: [8, 13] }
] as const;

/** Maps a step index to its phase metadata for injection into `data`. */
const getPhaseForIndex = (
  stepIndex: number
): { number: number; total: number; label: string } => {
  // First phase whose inclusive range contains the step; fall back to the first
  // (Math.max(0, -1) === 0 handles "no match" the same way as the manual loop).
  const matchIndex = dayZeroHighLevelSteps.findIndex(
    ({ range: [start, end] }) => stepIndex >= start && stepIndex <= end
  );
  const index = Math.max(0, matchIndex);
  return {
    number: index + 1,
    total: dayZeroHighLevelSteps.length,
    label: dayZeroHighLevelSteps.at(index)?.label ?? ''
  };
};

const rawSteps = [
  {
    id: 'open-org-selector',
    target: '[data-tour="open-org-selector"]',
    title: 'Select your organization',
    content:
      'In Moderne, an **organization** is a collection of related repositories. Choosing an organization makes it possible to take action on those repositories.\n\n(You can see the repositories associated with each organization by clicking the **Repositories** tab in the side nav.)\n\n**Click the selector** to see available organizations.',
    placement: 'bottom-start',
    skipBeacon: true,
    data: { advance: 'action' as const }
  },
  {
    id: 'choose-org',
    target: '[data-tour="choose-org"]',
    title: 'Choose an organization',
    content: createElement(ChooseOrgContent),
    placement: 'right-start',
    // The org selector is a wide centered modal; keep the tooltip pinned to the
    // right of it (don't let it auto-flip below the dialog and off-screen)
    // so the org list stays visible.
    floatingOptions: { flipOptions: false },
    skipBeacon: true,
    // No Back: this step lives inside the open org-selector dialog, which
    // focus-traps and aria-hides the tour tooltip. The natural way back is to
    // dismiss the dialog (Esc / its close button). On reload the dialog is
    // closed, so resume from the step that reopens it.
    data: { advance: 'action' as const, resumeAnchor: 'open-org-selector' }
  },
  {
    id: 'open-search',
    target: '[data-tour="open-search"]',
    title: 'Find a recipe',
    content:
      'A **recipe** is an automated change you run across your repositories — there are **thousands** to choose from, from simple cleanups to large-scale migrations.\n\nThe fastest way to find one is search.\n\n**Click the search bar** to begin.',
    placement: 'bottom',
    skipBeacon: true,
    data: { advance: 'action' as const, backTo: 'open-org-selector' }
  },
  {
    id: 'type-recipe',
    target: '[data-tour="type-recipe"]',
    title: 'Search for the recipe',
    content:
      'Let’s run **Common static analysis issues**. Type **“common static”** to find it.',
    placement: 'bottom',
    skipBeacon: true,
    // No Back: lives inside the open search dialog (see 'choose-org').
    data: { advance: 'action' as const, resumeAnchor: 'open-search' }
  },
  {
    id: 'choose-recipe',
    target: '[data-tour="choose-recipe"]',
    title: 'Select the recipe',
    content:
      'Select **“Common static analysis issues”** to view its details.\n\nIt fixes a broad set of static analysis issues while keeping your code working.',
    placement: 'bottom',
    skipBeacon: true,
    // No overlay so the results list stays scrollable; the other recipes are
    // made non-selectable during the tour in search-recipes instead.
    hideOverlay: true,
    // No Back: lives inside the open search dialog (see 'choose-org').
    data: { advance: 'action' as const, resumeAnchor: 'open-search' }
  },
  {
    id: 'recipe-details',
    target: '[data-tour="recipe-details"]',
    route: '/recipes/[recipeId]',
    // The recipe page lives behind a dynamic route the engine can't push
    // literally; this concrete URL lets Back return here from the results page.
    href: recipeDetailsUrl({ id: DAY_ZERO_RECIPE_ID }),
    title: 'The recipe details page',
    content:
      'This is the page for the recipe you selected. It explains what the recipe does, lists the repositories it will run against, and, if it’s a composite recipe, the smaller recipes it’s built from.',
    placement: 'bottom-start',
    skipBeacon: true,
    // The spotlight covers the whole card, including Dry run — block click-through
    // so the user reads here and advances with Next instead of starting the run
    // early (the dedicated dry-run step comes next).
    blockTargetInteraction: true,
    data: { advance: 'next' as const, backTo: 'open-search' }
  },
  {
    id: 'recipe-list',
    target: '[data-tour="recipe-list"]',
    route: '/recipes/[recipeId]',
    title: 'What the recipe will run',
    content:
      'Common static analysis issues is a **composite recipe**. That means it bundles many smaller recipes, with each recipe fixing one kind of issue. This list shows every recipe that will run across your repositories.',
    placement: 'top-start',
    skipBeacon: true,
    // Don't yank the page to the top of the (long) recipe-list table — highlight
    // it in place so the recipe-details context above stays visible.
    skipScroll: true,
    data: { advance: 'next' as const, backTo: 'recipe-details' }
  },
  {
    id: 'dry-run',
    target: '[data-tour="dry-run"]',
    route: '/recipes/[recipeId]',
    title: 'Try a dry run',
    content:
      'A dry run shows what the recipe would change **without modifying anything**, so it is completely safe to experiment. **Click Dry run** to start. (You may be asked for additional permissions.)',
    placement: 'bottom',
    skipBeacon: true,
    // The Dry run button sits at the top of the recipe page and is already in
    // view from the recipe-list step — no scroll needed.
    skipScroll: true,
    data: { advance: 'action' as const, backTo: 'recipe-details' }
  },
  {
    id: 'results-progress',
    target: '[data-tour="results-progress"]',
    route: '/results/[organization]/[recipeRunId]',
    title: 'Your dry run is processing',
    content:
      'This bar tracks progress as the recipe runs across your repositories. A **dry run** previews changes without modifying anything — results stream in below as each repository finishes.',
    placement: 'bottom-start',
    skipBeacon: true,
    // The progress row grows once the Cancel button renders during syncing;
    // extra spotlight padding keeps the bar + Cancel fully inside the highlight.
    spotlightPadding: 16,
    // No Back: recipe-details is a page away, and returning there only to go
    // forward again re-runs Dry run (a second run). The later results steps keep
    // Back since those are same-page moves.
    data: { advance: 'next' as const }
  },
  {
    id: 'results-tabs',
    target: '[data-tour="results-tabs"]',
    route: '/results/[organization]/[recipeRunId]',
    title: 'Explore the run',
    content:
      '**Results** lets you review generated code diffs and apply them by creating pull requests or committing changes.\n\n**Repository status** shows the progress and details for each repository in the run.\n\n**Data tables** provides structured outputs from the recipe that you can view or download (CSV, Excel).\n\n**Visualizations** lets you run pre-built Jupyter notebooks to create charts and insights from recipe data.',
    placement: 'bottom-start',
    skipBeacon: true,
    data: { advance: 'next' as const, backTo: 'results-progress' }
  },
  {
    id: 'results-tree',
    target: '[data-tour="results-tree"]',
    route: '/results/[organization]/[recipeRunId]',
    title: 'Your results show up here',
    content:
      'Every repository the recipe ran on, grouped by organization.\n\nThose with changes show a **result count** on the right. Open one to see what the recipe would change.\n\nIf the run is still finishing, wait a moment for counts to appear.',
    placement: 'right-start',
    skipBeacon: true,
    // Advances when the user opens a repository (wired in results-summary). When
    // the run finishes with zero results there is nothing to open, so the
    // results page completes the tour here instead (the "results show up here"
    // message is the graceful end).
    data: { advance: 'action' as const, backTo: 'results-progress' }
  },
  {
    id: 'results-diff',
    target: '[data-tour="results-diff"]',
    route: '/results/[organization]/[recipeRunId]',
    title: 'Review the changes',
    content:
      'This **file-by-file diff** shows exactly what the recipe would change, side by side. Nothing is committed: a dry run only previews the changes.\n\nScroll through to review each change.',
    placement: 'left-start',
    skipBeacon: true,
    data: { advance: 'next' as const, backTo: 'results-progress' }
  },
  {
    id: 'results-why',
    target: '[data-tour="results-why"]',
    route: '/results/[organization]/[recipeRunId]',
    title: 'See why any change was made',
    content:
      'Every change is traceable. **Click this chip** on any file to see exactly which recipe — and which rule — made the edit.',
    placement: 'left-start',
    skipBeacon: true,
    // The diff list is an inner overflow:auto scroll container; letting the tour
    // scroll to the (repeated) per-file attribution chip mis-measures the
    // spotlight. Keep the page put; `before` scrolls the pane instead.
    skipScroll: true,
    // Scrolling the diff on the prior step can hide the target chip above it.
    before: async () => {
      const chip = document.querySelector<HTMLElement>(
        '[data-tour="results-why"]'
      );
      if (chip) {
        scrollPaneUpTo(chip);
      }
    },
    data: { advance: 'action' as const, backTo: 'results-progress' }
  },
  {
    id: 'results-why-drawer',
    target: '[data-tour="results-why-drawer"]',
    route: '/results/[organization]/[recipeRunId]',
    title: 'The full story behind a change',
    content:
      'This panel shows the recipe that made the change, the specific rule it applied, and any configuration it used — so every edit is fully traceable.\n\nAnd that’s the tour!',
    placement: 'bottom-start',
    // Anchor below the drawer's content (it's spotlighting that content), and
    // pin it there so the tooltip doesn't flip up on top of it.
    floatingOptions: { flipOptions: false },
    skipBeacon: true,
    skipScroll: true,
    // No Back: lives inside the open "why" drawer (see 'choose-org'). On reload
    // the drawer is closed, but the opened repo persists in `?repo=`, so
    // results-why (its menu button) is back one step — no need to rewind further.
    data: { advance: 'next' as const, resumeAnchor: 'results-why' }
  }
];

export const dayZeroTourConfig: EngineTourConfig = {
  tourId: DAY_ZERO_TOUR_ID,
  storageKey: DAY_ZERO_STORAGE_KEY,
  highLevelSteps: dayZeroHighLevelSteps.map((phase) => ({
    label: phase.label,
    range: [phase.range[0], phase.range[1]]
  })),
  steps: rawSteps.map((step, index) => {
    const phase = getPhaseForIndex(index);
    return {
      ...step,
      data: {
        ...step.data,
        phaseNumber: phase.number,
        phaseTotal: phase.total,
        phaseLabel: phase.label
      }
    } as EngineTourStep;
  })
};
